import React from 'react';
import { TextField, InputAdornment, makeStyles, Box, IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import { ArrowDropUp, ArrowDropDown } from '@material-ui/icons';
import percentIcon from '../../../../images/sales-stream/icons/percent.svg';

// Regular expression to match numeric values between 0 and 100
const NUMERIC_REGEX = /^(100|[1-9]?[0-9])$/;
const STEP = 1;
const STEP_DIRECTION = {
  UP: 'up',
  DOWN: 'down',
};
const SUPPORTED_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];

const useStyles = makeStyles({
  arrowIcon: { color: '#fff' },
  iconButton: { borderRadius: '0px', height: '8px', width: '16px', overflow: 'hidden' },
  inputContainer: { color: '#fff', paddingRight: '9px' },
  inputOutline: { borderColor: '#fff !important' },
  inputLabel: { color: '#fff !important' },
  adornmentContainer: { display: 'flex', flexDirection: 'row', gap: '8px' },
});

function NumberInput({ id, label, min, max, error, value, onChange }) {
  const classes = useStyles();

  const handleInputChange = event => {
    const { value: newStringValue } = event.target;

    // Allow only numeric values or empty string
    if (newStringValue.match(NUMERIC_REGEX) || newStringValue === '') {
      onChange(newStringValue);
      return;
    }

    onChange(value);
  };

  const handleInputPaste = event => {
    // Prevent pasting non-numeric values
    const clipboardData = event.clipboardData.getData('text/plain');
    if (!clipboardData.match(NUMERIC_REGEX)) {
      event.preventDefault();
    }
  };

  const handleStep = direction => {
    if (value !== '' && value !== null && !Number.isNaN(Number(value))) {
      const isValidStep = {
        [STEP_DIRECTION.UP]: Number(value) < max,
        [STEP_DIRECTION.DOWN]: Number(value) > min,
      };
      const newValue = {
        [STEP_DIRECTION.UP]: Number(value) + STEP,
        [STEP_DIRECTION.DOWN]: Number(value) - STEP,
      };

      if (isValidStep[direction]) {
        onChange(String(newValue[direction]));
      }
    } else if (direction === STEP_DIRECTION.UP) {
      onChange(String(min));
    }
  };

  const handleInputKeyDown = event => {
    const { key } = event;

    if (SUPPORTED_KEYS.includes(key)) {
      event.preventDefault();
    }

    switch (key) {
      case 'ArrowUp':
        handleStep(STEP_DIRECTION.UP);
        break;
      case 'ArrowDown':
        handleStep(STEP_DIRECTION.DOWN);
        break;
      case 'PageUp':
        handleStep(STEP_DIRECTION.UP);
        break;
      case 'PageDown':
        handleStep(STEP_DIRECTION.DOWN);
        break;
      case 'Home':
        onChange(String(max));
        break;
      case 'End':
        onChange(String(min));
        break;
      default:
        break;
    }
  };

  return (
    <TextField
      fullWidth
      id={id}
      autoComplete="off"
      inputMode="numeric"
      label={label}
      size="small"
      type="text"
      value={value ?? ''}
      variant="outlined"
      InputLabelProps={{ shrink: true, classes: { root: classes.inputLabel } }}
      InputProps={{
        classes: { root: classes.inputContainer, notchedOutline: classes.inputOutline },
        endAdornment: (
          <InputAdornment position="end">
            <Box className={classes.adornmentContainer}>
              <img src={percentIcon} alt="percent icon" />

              <Box display="flex" flexDirection="column">
                <IconButton
                  aria-label="Increase percentage"
                  className={classes.iconButton}
                  size="small"
                  onClick={() => handleStep(STEP_DIRECTION.UP)}
                >
                  <ArrowDropUp className={classes.arrowIcon} />
                </IconButton>
                <IconButton
                  aria-label="Decrease percentage"
                  className={classes.iconButton}
                  size="small"
                  onClick={() => handleStep(STEP_DIRECTION.DOWN)}
                >
                  <ArrowDropDown className={classes.arrowIcon} />
                </IconButton>
              </Box>
            </Box>
          </InputAdornment>
        ),
        inputProps: { pattern: '[0-9]*' },
      }}
      error={Boolean(error)}
      helperText={error}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onPaste={handleInputPaste}
    />
  );
}

NumberInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  error: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

NumberInput.defaultProps = {
  min: 0,
  max: 100,
  error: undefined,
  value: null,
};

export default NumberInput;
